import type { ReactNode } from 'react';
import { Box, Button, Flex, Modal, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { MutationButton } from './MutationButton';

interface ConfirmActionModalProps {
  opened: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  contextLine?: ReactNode;
  message: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  danger?: boolean;
  loading?: boolean;
  isPaused?: boolean;
  confirmDisabled?: boolean;
  children?: ReactNode;
}

export const ConfirmActionModal = ({
  opened,
  onClose,
  onConfirm,
  title,
  contextLine,
  message,
  confirmLabel,
  cancelLabel,
  danger,
  loading,
  isPaused,
  confirmDisabled,
  children
}: ConfirmActionModalProps) => {
  const { t } = useTranslation();

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      radius="lg"
      withCloseButton={false}
      overlayProps={{ backgroundOpacity: 0.55 }}
      styles={{
        content: {
          backgroundColor: 'var(--color-bg-surface)',
          boxShadow: '0px 16px 20px rgba(0, 0, 0, 0.15)'
        },
        body: { padding: 0 }
      }}
    >
      <Box p={{ base: 20, sm: 24 }}>
        <Flex direction="column" gap={{ base: 16, sm: 20 }}>
          <Stack gap={6}>
            <Text fz={18} fw={700} style={{ color: 'var(--color-text-primary)' }}>
              {title}
            </Text>
            {contextLine && <div>{contextLine}</div>}
          </Stack>

          <Box style={{ fontSize: 13, lineHeight: '20px', color: 'var(--color-text-secondary)' }}>
            {message}
          </Box>

          {children}

          <Flex
            direction={{ base: 'column-reverse', sm: 'row' }}
            justify={{ sm: 'flex-end' }}
            gap={{ base: 8, sm: 12 }}
          >
            <Button variant="outline" onClick={onClose} w={{ base: '100%', sm: 'auto' }}>
              {cancelLabel ?? t('common.cancel')}
            </Button>
            <MutationButton
              isPending={loading}
              isPaused={isPaused}
              disabled={confirmDisabled}
              onClick={onConfirm}
              w={{ base: '100%', sm: 'auto' }}
              color={danger ? 'red' : undefined}
            >
              {confirmLabel}
            </MutationButton>
          </Flex>
        </Flex>
      </Box>
    </Modal>
  );
};
